// E12-t29 (web half) — THE SIGN-IN PAGE.   route: /signin
//
// E14-t1 rebuilt this on the ASTRYX COMPONENT LAYER. What changed is the
// rendering and nothing else: the product depended on @astryxdesign/core and
// imported only its stylesheet, so every control here was a hand-styled `input`
// and `button` wearing the design system's tokens. The controls are now the
// design system's own.
//
// THE THREE RULES BELOW ARE NOT STYLING AND MUST SURVIVE ANY RESTYLE. They are
// the reason this file has a header at all.
//
// Contract: git-host/src/login.ts — POST /api/auth/{signup,signin}. Everything
//   this page says about what an account IS comes from that file's header and
//   from platform/src/accounts.ts, because the page must not describe a product
//   the server does not implement:
//     · SIGN-UP IS THE FIRST-SESSION PATH. There is no bootstrap secret, so the
//       first person to reach a new deployment creates an account, and what that
//       grants is the FLOOR of the permission system — an account and a personal
//       org that admits no second member. The page says exactly that, because a
//       "Create an account" button that looks like it joins an existing team is a
//       promise the membership guard will break.
//     · ONE REFUSAL. A wrong password, an unknown email and a taken address are
//       the same bytes on the wire, deliberately, so nobody can ask this service
//       who has an account here. The page therefore renders the server's sentence
//       AS IT ARRIVES and never dresses it up into "that email is already taken"
//       or "no such account" — doing so would rebuild in the client the very
//       enumeration oracle the server refused to be. The refusal is the Banner's
//       ONLY text for that reason: nothing is added around the server's words.
// Contract: vault/Apps/Forge/Pages/Sign In.md — f1–f4, and f5 (this rebuild).
// Contract: vault/Apps/Forge/Forge.md q1 (archived) — "Every surface sits behind
//   membership at launch". This is the ONE anonymous page in the app, which is
//   why it is rendered OUTSIDE AppShell: the shell's own unauthenticated face is a
//   prompt with no chrome at all (an org list is itself a disclosure), and this
//   page is what that prompt now leads to.
// Contract: vault/Flows/Tenancy and Guards.md f5 — accounts are "email plus
//   provider login as identity only". There is no provider button here because
//   there is no provider route: an OAuth button that posts nowhere is the kind of
//   finished-looking surface this repository exists to stop.
// Contract: vault/Backlog/E12 Testable Surface and Authoring.md t29, and t32 —
//   the sign-up door is anonymous and unmetered, with no rate limit, lockout,
//   email verification or invite gate anywhere in the repo. This page cannot fix
//   that (a client-side limit is not one) and does not pretend to.
//
// PRESENTATIONAL, like every other page here: the transport is injected
// (src/api/auth.ts) and the container decides where a successful sign-in GOES.
// It holds the typed password in component state for exactly as long as the form
// is mounted and never writes it anywhere else — no storage, no url, no log.
import { useState, type FormEvent } from 'react';

import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';

import { AuthRefusal, type AuthClient } from '../api/auth';
import { Eyebrow } from '../components/Eyebrow';
import type { SessionPayload } from '../shell/AppShell';

// E14-t6 — signin.css is gone. Everything it held was either a control the design
// system ships (Card, TextInput, Button, Banner, Stack — E14-t1 did that half) or
// a SECOND copy of the `.dj-signin` centring rule that src/shell/shell.css
// already carried for AppShell's members-only prompt. shell.css is imported
// globally by src/main.tsx, so the one surviving rule reaches this page from
// there. Its old `.dj-signin > *` width clamp is redundant: the Card below sets
// width="100%" and maxWidth="26rem" itself.

export interface SignInPageProps {
  client: AuthClient;
  /** the identity, handed up. The container navigates; this page does not. */
  onSignedIn: (session: SessionPayload) => void;
  /** why the reader was sent here, when something sent them (the device flow) */
  note?: string | null;
}

type Mode = 'signin' | 'signup';

/** The one sentence shown when the server sent no message of its own. Everything
 *  else on this surface is the CONTROL PLANE's wording, relayed. */
const UNREACHABLE =
  'The forge could not be reached, so nothing was signed in. Try again in a moment.';

export function SignInPage({ client, onSignedIn, note = null }: SignInPageProps) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const creating = mode === 'signup';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      const session = creating
        ? await client.signUp({ email, password, displayName: displayName.trim() || undefined })
        : await client.signIn({ email, password });
      // The password leaves state the moment it has been spent. It is not a
      // security boundary (the process could be inspected either way); it is the
      // same hygiene the cookie's HttpOnly flag is about — nothing keeps a
      // credential it has no further use for.
      setPassword('');
      onSignedIn(session);
    } catch (err: unknown) {
      // The server's own sentence, verbatim. A refusal it did not word (a network
      // failure) gets the one sentence above and no invented server answer.
      setRefusal(err instanceof AuthRefusal ? err.message : UNREACHABLE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dj-signin" data-surface="signin" data-page="signin">
      {/* E15-t5 — bordered, not floated: the prototype defines a shadow token and
          declines to spend it on a card. */}
      <Card padding={6} elevation="none" maxWidth="26rem" width="100%">
        <form onSubmit={submit} aria-label="Sign in">
          <Stack direction="vertical" gap={5}>
            <Stack direction="vertical" gap={1}>
              {/* E15-t2 — the same eyebrow AppShell's members-only prompt wears,
                  and it is the same surface seen from the other side; one motif,
                  one component (src/components/Eyebrow.tsx). */}
              <Eyebrow>docujoint forge</Eyebrow>
              <Heading level={1} type="display-3">
                {creating ? 'Create an account' : 'Sign in'}
              </Heading>
              <Text type="body" color="secondary">
                {creating
                  ? 'A new account comes with one personal organization and nothing else. Every other organization stays members-only until one of its admins adds you.'
                  : 'Every knowledge base in the forge sits behind membership. Sign in to reach your organizations.'}
              </Text>
            </Stack>

            {note === null ? null : (
              <Banner status="info" title={note} container="card" data-note="purpose" />
            )}

            {refusal === null ? null : (
              // status="error" is what gives this role="alert" (Banner.tsx
              // statusRole), which is the contract signin.test.tsx reads it by.
              // The server's sentence is the banner's ONLY text — see ONE REFUSAL.
              <Banner status="error" title={refusal} container="card" />
            )}

            <Stack direction="vertical" gap={3}>
              <TextInput
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                isRequired
                htmlName="email"
              />
              <TextInput
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                isRequired
                htmlName="password"
              />
              {creating ? (
                // OPTIONAL, and labelled as what it actually does: accounts.ts
                // derives only the personal org's NAME from it, and falls back to
                // the address.
                <TextInput
                  label="Display name"
                  type="text"
                  value={displayName}
                  onChange={setDisplayName}
                  isOptional
                  htmlName="displayName"
                  description="Names your personal organization. Defaults to your email address."
                />
              ) : null}
            </Stack>

            <Stack direction="vertical" gap={2}>
              <Button
                type="submit"
                variant="primary"
                width="100%"
                isLoading={busy}
                label={creating ? 'Create account' : 'Sign in'}
              />
              <Button
                type="button"
                variant="ghost"
                width="100%"
                label={creating ? 'I already have an account' : 'Create an account'}
                onClick={() => {
                  setMode(creating ? 'signin' : 'signup');
                  // A refusal belongs to the request that earned it, not the form.
                  setRefusal(null);
                }}
              />
            </Stack>
          </Stack>
        </form>
      </Card>
    </div>
  );
}

export default SignInPage;
